import { ArrowRightIcon, CheckCircle2Icon, ChevronDownIcon, RefreshCwIcon } from 'lucide-react';
import { useEffect } from 'react';
import { Link, useSearchParams } from 'react-router';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useAuth } from '@/lib/auth';
import { useBranding } from '@/lib/branding';
import { fmtDate, fmtDateTime, fmtRelative } from '@/lib/format';
import { COMPONENT_LABEL, INCIDENT_STATUS_LABEL, isClosedStatus, KIND_LABEL, RELEASE_KIND_LABEL, SEVERITY_LABEL, usePublicStatus, type ComponentState, type Incident, type IncidentSeverity, type OverallState, type Release } from '@/lib/status';
import { cn } from '@/lib/utils';

type Health = 'good' | 'warn' | 'bad' | 'info';
const STATE_HEALTH: Record<ComponentState, Health> = { operational: 'good', degraded: 'warn', outage: 'bad', maintenance: 'info', unknown: 'warn' };
const STATE_LABEL: Record<ComponentState, string> = { operational: 'Operational', degraded: 'Degraded', outage: 'Outage', maintenance: 'Maintenance', unknown: 'Unknown' };
const HEALTH_BG: Record<Health, string> = { good: 'bg-success', warn: 'bg-warning', bad: 'bg-destructive', info: 'bg-primary' };
const HEALTH_TEXT: Record<Health, string> = { good: 'text-success', warn: 'text-warning', bad: 'text-destructive', info: 'text-primary' };
const HEALTH_BORDER: Record<Health, string> = { good: 'border-l-success', warn: 'border-l-warning', bad: 'border-l-destructive', info: 'border-l-primary' };
const OVERALL: Record<OverallState, { title: string; sub: string; health: Health }> = {
  operational: { title: 'All systems operational', sub: 'Every part of Crewline is up and responding normally.', health: 'good' },
  degraded: { title: 'Some systems are degraded', sub: 'Crewline is up, but part of it is slow or partially unavailable.', health: 'warn' },
  major_outage: { title: 'Major outage', sub: 'A core part of Crewline is unavailable. We are on it.', health: 'bad' },
  maintenance: { title: 'Maintenance in progress', sub: 'Planned work is under way. Some features may be briefly unavailable.', health: 'info' },
};
const SEVERITY_VARIANT: Record<IncidentSeverity, 'muted' | 'warning' | 'danger'> = { none: 'muted', minor: 'warning', major: 'danger', critical: 'danger' };

const fmtTime = (iso: string): string => new Date(iso).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });

function Dot({ health, className }: { health: Health; className?: string }) {
  return <span className={cn('inline-block size-2 shrink-0', HEALTH_BG[health], className)} aria-hidden />;
}

const EYEBROW = 'text-muted-foreground text-xs font-semibold tracking-wide uppercase';

/** Public status page at /status: no sign-in, polls every minute. The operations console serves the same feed. */
export function StatusPage() {
  const q = usePublicStatus();
  const { status } = useAuth();
  const { branding } = useBranding();
  const [params] = useSearchParams();
  const incidentParam = params.get('incident');
  const d = q.data;

  useEffect(() => {
    document.title = `System status · ${branding.appName}`;
  }, [branding.appName]);
  useEffect(() => {
    if (!incidentParam || !d) return;
    document.getElementById(`incident-${incidentParam}`)?.scrollIntoView({ block: 'start', behavior: 'smooth' });
  }, [incidentParam, d]);

  const open = (d?.incidents ?? []).filter((i) => !isClosedStatus(i.status) && !(i.kind === 'maintenance' && i.status === 'scheduled'));
  const scheduled = (d?.incidents ?? []).filter((i) => i.kind === 'maintenance' && i.status === 'scheduled');
  const past = (d?.incidents ?? []).filter((i) => isClosedStatus(i.status));
  const overall = d ? OVERALL[d.overall] : null;

  return (
    <div className="bg-background text-foreground flex min-h-dvh flex-col">
      <div className={cn('h-0.5', overall ? HEALTH_BG[overall.health] : 'bg-muted')} aria-hidden />
      <header className="border-b px-5 sm:px-8">
        <div className="mx-auto flex h-16 w-full max-w-4xl items-center justify-between gap-4">
          <Link to="/status" className="flex items-center gap-3">
            {branding.logoUrl ? <img src={branding.logoUrl} alt="" className="h-8 w-auto" /> : null}
            <span className="flex flex-col leading-none">
              <span className="font-display text-2xl">{branding.appName}</span>
              <span className="text-muted-foreground mt-0.5 text-[10px] font-medium tracking-[0.18em] uppercase">System status</span>
            </span>
          </Link>
          <Button asChild variant="outline" size="sm">
            <Link to={status === 'authenticated' ? '/jobs' : '/login'}>
              {status === 'authenticated' ? 'Back to the app' : 'Sign in'} <ArrowRightIcon />
            </Link>
          </Button>
        </div>
      </header>

      <main className="mx-auto flex w-full max-w-4xl flex-1 flex-col gap-10 px-5 py-8 sm:px-8 sm:py-12">
        {q.isLoading || !d || !overall ? (
          q.isError ? (
            <section className="border border-l-4 border-l-warning bg-card px-6 py-6">
              <h1 className="font-display text-3xl leading-none">Status unavailable</h1>
              <p className="text-muted-foreground mt-2 text-sm">The status feed could not be reached. That usually means the operations service itself is down.</p>
              <p className="text-muted-foreground mt-3 font-mono text-xs">{q.error instanceof Error ? q.error.message : 'Unknown error'}</p>
            </section>
          ) : (
            <Skeleton className="h-28 w-full" />
          )
        ) : (
          <section className={cn('bg-card flex flex-col gap-2 border border-l-4 px-6 py-6', HEALTH_BORDER[overall.health])} aria-live="polite">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <h1 className="font-display flex items-center gap-3 text-3xl leading-none sm:text-4xl">
                <Dot health={overall.health} className="size-3" />
                {overall.title}
              </h1>
              <button type="button" onClick={() => void q.refetch()} className="text-muted-foreground hover:text-foreground flex items-center gap-1.5 text-xs" title="Refresh now">
                <RefreshCwIcon className={cn('size-3.5', q.isFetching && 'animate-spin')} /> Checked {fmtTime(d.generatedAt)} · refreshes every minute
              </button>
            </div>
            <p className="text-muted-foreground text-sm">{overall.sub}</p>
          </section>
        )}

        <section className="flex flex-col gap-3" aria-labelledby="components">
          <h2 id="components" className={EYEBROW}>
            Components
          </h2>
          <ul className="bg-card divide-y border">
            {(d?.components ?? Array.from({ length: 6 }, () => null)).map((c, i) =>
              c ? (
                <li key={c.key} className="flex items-start justify-between gap-4 px-5 py-3.5">
                  <div className="flex min-w-0 items-start gap-3">
                    <Dot health={STATE_HEALTH[c.state]} className="mt-1.5" />
                    <div className="min-w-0">
                      <div className="text-sm font-medium">{c.name}</div>
                      <div className="text-muted-foreground text-xs">{c.description}</div>
                    </div>
                  </div>
                  <div className="shrink-0 text-right">
                    <div className={cn('text-sm font-medium', HEALTH_TEXT[STATE_HEALTH[c.state]])}>{STATE_LABEL[c.state]}</div>
                    {c.detail ? <div className="text-muted-foreground text-xs tabular-nums">{c.detail}</div> : null}
                  </div>
                </li>
              ) : (
                <li key={i} className="px-5 py-3.5">
                  <Skeleton className="h-8 w-full" />
                </li>
              ),
            )}
          </ul>
        </section>

        {open.length > 0 ? (
          <section className="flex flex-col gap-3" aria-labelledby="active">
            <h2 id="active" className={EYEBROW}>
              Active incidents
            </h2>
            {open.map((i) => (
              <IncidentCard key={i.id} incident={i} highlighted={incidentParam === i.id} />
            ))}
          </section>
        ) : null}

        {scheduled.length > 0 ? (
          <section className="flex flex-col gap-3" aria-labelledby="scheduled">
            <h2 id="scheduled" className={EYEBROW}>
              Scheduled maintenance
            </h2>
            {scheduled.map((i) => (
              <IncidentCard key={i.id} incident={i} highlighted={incidentParam === i.id} />
            ))}
          </section>
        ) : null}

        <section className="flex flex-col gap-3" aria-labelledby="past">
          <h2 id="past" className={EYEBROW}>
            Past incidents · last 30 days
          </h2>
          {q.isLoading ? (
            <Skeleton className="h-16 w-full" />
          ) : past.length === 0 ? (
            <div className="bg-card text-muted-foreground flex items-center gap-3 border px-5 py-4 text-sm">
              <CheckCircle2Icon className="text-success size-4" /> No incidents in the last 30 days.
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              {past.map((i) => (
                <IncidentCard key={i.id} incident={i} highlighted={incidentParam === i.id} collapsed={incidentParam !== i.id} />
              ))}
            </div>
          )}
        </section>

        <section className="flex flex-col gap-3" aria-labelledby="releases" id="releases">
          <h2 className={EYEBROW}>Release notes</h2>
          {q.isLoading ? (
            <Skeleton className="h-24 w-full" />
          ) : (d?.releases.length ?? 0) === 0 ? (
            <p className="bg-card text-muted-foreground border px-5 py-4 text-sm">Release notes will appear here.</p>
          ) : (
            <ol className="bg-card divide-y border">{d?.releases.map((r) => <ReleaseRow key={r.id} release={r} />)}</ol>
          )}
        </section>
      </main>

      <footer className="border-t px-5 sm:px-8">
        <div className="text-muted-foreground mx-auto flex w-full max-w-4xl flex-col gap-2 py-5 text-xs sm:flex-row sm:items-center sm:justify-between">
          <span>
            Versions · web app {typeof __APP_VERSION__ === 'string' ? __APP_VERSION__ : 'dev'} · tenant API {d?.versions.tenantApi ?? '—'} · admin API {d?.versions.adminApi ?? '—'}
          </span>
          <span>
            {branding.appName} · {new Date().getFullYear()}
          </span>
        </div>
      </footer>
    </div>
  );
}

function IncidentCard({ incident: i, highlighted, collapsed }: { incident: Incident; highlighted?: boolean; collapsed?: boolean }) {
  const closed = isClosedStatus(i.status);
  return (
    <details id={`incident-${i.id}`} open={!collapsed} className={cn('group bg-card scroll-mt-6 border', highlighted && 'border-primary')}>
      <summary className="flex cursor-pointer flex-wrap items-start justify-between gap-3 px-5 py-4 select-none [&::-webkit-details-marker]:hidden">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant={closed ? 'muted' : SEVERITY_VARIANT[i.severity]}>{i.kind === 'maintenance' ? KIND_LABEL.maintenance : i.kind === 'notice' ? KIND_LABEL.notice : SEVERITY_LABEL[i.severity]}</Badge>
            <Badge variant="outline">{INCIDENT_STATUS_LABEL[i.status]}</Badge>
          </div>
          <h3 className="font-display mt-2 text-xl leading-tight">{i.title}</h3>
          <p className="text-muted-foreground mt-1 text-xs">
            {i.kind === 'maintenance' && i.status === 'scheduled' ? `Scheduled for ${fmtDateTime(i.startsAt)}${i.endsAt ? ` – ${fmtTime(i.endsAt)}` : ''}` : closed ? `${fmtDateTime(i.startsAt)} → resolved ${i.resolvedAt ? fmtRelative(i.resolvedAt) : ''}` : `Started ${fmtDateTime(i.startsAt)}`}
            {i.components.length ? ` · affects ${i.components.map((c) => COMPONENT_LABEL[c]).join(', ')}` : ''}
          </p>
        </div>
        <ChevronDownIcon className="text-muted-foreground mt-1 size-4 shrink-0 transition-transform group-open:rotate-180" />
      </summary>
      <div className="border-t px-5 py-4">
        {i.body ? <p className="mb-4 max-w-2xl text-sm leading-relaxed whitespace-pre-wrap">{i.body}</p> : null}
        {i.updates.length > 0 ? (
          <ol className="flex flex-col gap-3 border-l pl-4">
            {[...i.updates].reverse().map((u) => (
              <li key={u.id} className="relative">
                <span className="bg-foreground absolute top-2 -left-[1.3rem] size-2" aria-hidden />
                <div className="text-muted-foreground flex flex-wrap items-baseline gap-x-2 text-xs">
                  <span className="text-foreground font-medium">{INCIDENT_STATUS_LABEL[u.status]}</span>
                  <time dateTime={u.createdAt}>{fmtDateTime(u.createdAt)}</time>
                </div>
                <p className="mt-1 text-sm leading-relaxed whitespace-pre-wrap">{u.body}</p>
              </li>
            ))}
          </ol>
        ) : (
          <p className="text-muted-foreground text-xs">No updates posted yet.</p>
        )}
      </div>
    </details>
  );
}

function ReleaseRow({ release: r }: { release: Release }) {
  const lines = (r.notes ?? '').split(/\r?\n/).filter((l) => l.trim().length > 0);
  const bullets = lines.filter((l) => /^\s*[-*•]\s+/.test(l)).map((l) => l.replace(/^\s*[-*•]\s+/, ''));
  const paragraphs = lines.filter((l) => !/^\s*[-*•]\s+/.test(l));
  return (
    <li id={`release-${r.id}`} className="grid gap-3 px-5 py-4 sm:grid-cols-[8rem_1fr]">
      <div>
        <div className="font-mono text-sm font-semibold">v{r.version.replace(/^v/i, '')}</div>
        <div className="text-muted-foreground text-xs">{fmtDate(r.releasedAt)}</div>
        {r.kind !== 'release' ? (
          <Badge variant={r.kind === 'hotfix' ? 'warning' : 'muted'} className="mt-1">
            {RELEASE_KIND_LABEL[r.kind]}
          </Badge>
        ) : null}
      </div>
      <div className="min-w-0">
        <h3 className="text-sm font-medium">{r.title}</h3>
        {paragraphs.length ? <p className="text-muted-foreground mt-1 text-sm leading-relaxed whitespace-pre-wrap">{paragraphs.join('\n')}</p> : null}
        {bullets.length ? (
          <ul className="mt-2 flex list-disc flex-col gap-1 pl-5 text-sm leading-relaxed">
            {bullets.map((b, i) => (
              <li key={i}>{b}</li>
            ))}
          </ul>
        ) : null}
      </div>
    </li>
  );
}
